import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import * as LucideIcons from 'lucide-react';
import { X, CheckCircle, XCircle, ChevronDown, ChevronRight, Save, Shield } from 'lucide-react';
import clsx from 'clsx';
import { apiService } from '../../../../components/api/apiService';
import { useAccessTheme } from '../AccessManagement/useAccessTheme';

export interface RoutePrivilege {
    id: string;
    uid: string;
    label: string;
    path: string | null;
    icon: string;
    hasAccess: boolean;
    children?: RoutePrivilege[];
}

interface Props {
    roleName: string;
    roleId: string;
    tenantId: string;
    routes: RoutePrivilege[];
    onClose: () => void;
}

const ViewRoutePrivilegesModal: React.FC<Props> = ({
    roleName,
    roleId,
    tenantId,
    routes,
    onClose
}) => {
    const t = useAccessTheme();
    const [editedRoutes, setEditedRoutes] = useState<RoutePrivilege[]>(routes);
    const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const toggleAccess = (target: RoutePrivilege, nodes = editedRoutes): RoutePrivilege[] => {
        return nodes.map((r) => {
            if (r.uid === target.uid) return { ...r, hasAccess: !r.hasAccess };
            if (r.children) return { ...r, children: toggleAccess(target, r.children) };
            return r;
        });
    };

    const handleToggle = (route: RoutePrivilege) => {
        setEditedRoutes(toggleAccess(route));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await apiService.post('/api/RoutePermission/upsert', {
                tenantId,
                roleId,
                routes: editedRoutes
            }, 'backend');
            setToastMessage('Permissions saved successfully.');
        } catch (error) {
            console.error('Save failed:', error);
            setToastMessage('Failed to save permissions.');
        } finally {
            setSaving(false);
            setTimeout(() => setToastMessage(null), 2000);
        }
    };

    const selectedRoute = findRoute(editedRoutes, selectedRouteId);

    return createPortal(
        <div className={`fixed inset-0 z-[100] flex items-center justify-center p-4 ${t.overlay}`}>
            <div className={`rounded-xl shadow-xl w-full max-w-5xl max-h-[92vh] overflow-hidden border relative ${t.panel}`}>
                <div className={`flex items-center justify-between px-5 py-4 border-b ${t.header}`}>
                    <div>
                        <h2 className={`text-lg font-semibold ${t.title}`}>
                            Route Privileges — <span className={t.accentText}>{roleName}</span>
                        </h2>
                        <p className={`text-xs mt-0.5 ${t.muted}`}>Configure feature access for this role.</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 ${t.btnPrimary}`}
                        >
                            <Save className="w-4 h-4" />
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button type="button" onClick={onClose} className={`p-1.5 rounded-lg ${t.closeBtn}`}>
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {toastMessage && (
                    <div className={`absolute top-4 right-6 z-50 text-sm px-3 py-2 rounded-lg border ${t.isLight ? 'bg-white border-neutral-200 shadow' : 'bg-neutral-800 border-neutral-700'}`}>
                        {toastMessage}
                    </div>
                )}

                <div className="flex max-h-[calc(92vh-80px)] overflow-hidden">
                    <div className={`w-full md:w-2/3 overflow-y-auto p-5 border-r ${t.border}`}>
                        <RouteTree
                            routes={editedRoutes}
                            onToggle={handleToggle}
                            selected={selectedRouteId}
                            onSelect={setSelectedRouteId}
                            t={t}
                        />
                    </div>

                    <div className={`hidden md:block w-1/3 p-5 ${t.isLight ? 'bg-neutral-50' : 'bg-neutral-950/40'}`}>
                        {selectedRoute ? (
                            <div className="space-y-3">
                                <h4 className={`font-semibold border-b pb-2 ${t.title} ${t.border}`}>Route Details</h4>
                                <div>
                                    <p className={`text-xs ${t.muted}`}>UID</p>
                                    <p className={`text-sm font-mono mt-0.5 ${t.body}`}>{selectedRoute.uid}</p>
                                </div>
                                <div>
                                    <p className={`text-xs ${t.muted}`}>Label</p>
                                    <p className={`text-sm mt-0.5 ${t.title}`}>{selectedRoute.label}</p>
                                </div>
                                <div>
                                    <p className={`text-xs ${t.muted}`}>Path</p>
                                    <p className={`text-sm mt-0.5 font-mono ${t.muted}`}>{selectedRoute.path || '—'}</p>
                                </div>
                                <div className="flex items-center gap-2 pt-1">
                                    <span className={`text-xs ${t.muted}`}>Access:</span>
                                    {selectedRoute.hasAccess ? (
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${t.badgeAccent}`}>
                                            <CheckCircle className="w-3 h-3" /> Granted
                                        </span>
                                    ) : (
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${t.isLight ? 'bg-red-50 text-red-600 border-red-200' : 'bg-red-900/30 text-red-400 border-red-800/50'}`}>
                                            <XCircle className="w-3 h-3" /> Denied
                                        </span>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-center opacity-60">
                                <Shield className={`w-10 h-10 mb-2 ${t.muted}`} />
                                <p className={`text-xs ${t.muted}`}>Select a route to view details.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

const RouteTree: React.FC<{
    routes: RoutePrivilege[];
    onToggle: (route: RoutePrivilege) => void;
    selected: string | null;
    onSelect: (uid: string) => void;
    t: ReturnType<typeof useAccessTheme>;
}> = ({ routes, onToggle, selected, onSelect, t }) => {
    const [expanded, setExpanded] = useState<{ [uid: string]: boolean }>({});

    return (
        <ul className="space-y-1">
            {routes.map((route) => {
                const isExpanded = expanded[route.uid];
                const IconComponent = (LucideIcons as any)[route.icon] || LucideIcons.SquareDot;
                const isSelected = selected === route.uid;

                return (
                    <li key={route.uid}>
                        <div
                            className={clsx(
                                'flex items-center justify-between rounded-lg px-3 py-2 cursor-pointer border transition-colors',
                                isSelected
                                    ? t.isLight
                                        ? 'bg-indigo-50 border-indigo-200'
                                        : 'bg-indigo-900/20 border-indigo-500/40'
                                    : `border-transparent ${t.rowHover}`
                            )}
                            onClick={() => onSelect(route.uid)}
                        >
                            <div className="flex items-center gap-2 min-w-0">
                                {route.children && route.children.length > 0 ? (
                                    <button
                                        type="button"
                                        className={t.muted}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setExpanded((prev) => ({ ...prev, [route.uid]: !isExpanded }));
                                        }}
                                    >
                                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                    </button>
                                ) : (
                                    <span className="w-4 h-4" />
                                )}
                                <IconComponent className={`w-4 h-4 shrink-0 ${t.accentText}`} />
                                <span className={`text-sm font-medium truncate ${t.title}`}>{route.label}</span>
                            </div>
                            <button
                                type="button"
                                className={clsx(
                                    'flex items-center justify-center w-7 h-7 rounded-full',
                                    route.hasAccess
                                        ? 'text-green-600 hover:bg-green-500/10'
                                        : 'text-red-500 hover:bg-red-500/10'
                                )}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggle(route);
                                }}
                            >
                                {route.hasAccess ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                            </button>
                        </div>
                        {route.children && isExpanded && (
                            <div className="ml-4 mt-1">
                                <RouteTree
                                    routes={route.children}
                                    onToggle={onToggle}
                                    selected={selected}
                                    onSelect={onSelect}
                                    t={t}
                                />
                            </div>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};

function findRoute(routes: RoutePrivilege[], uid: string | null): RoutePrivilege | undefined {
    if (!uid) return undefined;
    for (const r of routes) {
        if (r.uid === uid) return r;
        if (r.children) {
            const found = findRoute(r.children, uid);
            if (found) return found;
        }
    }
    return undefined;
}

export default ViewRoutePrivilegesModal;
